import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { useAccount, useDisconnect, useSignMessage } from 'wagmi';
import { trackAuth } from '../../utils/analytics';
import * as authBridge from './authBridge';
import { performLogin, setApiKey, setAuthWallet } from '../../utils/api';
import { clearSession as clearStoredSession, exchangeProviderToken, getNonce, signIn } from './authService';
import { PLATFORM } from '../device.js';
import { fetchReputation } from '../../utils/reputation.js';

// NOTE: SDK imports moved to dynamic loading in authBridge.js to prevent import errors

// Development-only flag for agent logging (disabled in production)

// Create the auth context
const AuthContext = createContext();

// Context for RainbowKit modal (provided by Web3Provider)
// In native mode, this will be null/undefined
export const RainbowKitModalContext = createContext(null);

// Custom hook to use the auth context
// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => useContext(AuthContext);

// Auth provider component
export const AuthProvider = ({ children }) => {
  
  const { address, isConnected, isConnecting, connector } = useAccount();
  const { disconnect } = useDisconnect();
  // Get openConnectModal from context (may be null in native mode)
  const openConnectModal = useContext(RainbowKitModalContext);
  const { signMessageAsync } = useSignMessage();

  // Native Auth State (Farcaster / World / Base). Initialize from the durable
  // API-key session so a refresh renders the wallet immediately without a
  // signature popup while wagmi reconnects.
  const [nativeUser, setNativeUser] = useState(() => {
    if (typeof window === 'undefined') return null;
    const storedWallet = localStorage.getItem('dcw_wallet') || localStorage.getItem('dcw_auth_wallet');
    const storedApiKey = localStorage.getItem('dcw_api_key') || localStorage.getItem('dcw_token');
    if (!storedWallet || !storedApiKey) return null;
    return {
      username: storedWallet,
      custody_address: storedWallet,
      platform: 'web',
    };
  });
  const [isNativeLoading, setIsNativeLoading] = useState(true);
  
  // Reputation State
  const [reputation, setReputation] = useState(null);
  const [isLoadingReputation, setIsLoadingReputation] = useState(false);
  
  // Track if we've already completed SIWE for this address to prevent double-login
  // CRITICAL: Also persist in localStorage to survive re-renders and page reloads
  const siweCompletedRef = useRef(null);
  const [authError, setAuthError] = useState(null);
  
  // Restore the last wallet session on mount. The 90-day API key is the
  // durable credential; the wallet signature is only needed to create it.
  useEffect(() => {
    const storedCompletedAddress = localStorage.getItem('dcw_siwe_completed');
    const storedWallet = localStorage.getItem('dcw_wallet') || localStorage.getItem('dcw_auth_wallet');
    const storedApiKey = localStorage.getItem('dcw_api_key') || localStorage.getItem('dcw_token');

    if (storedCompletedAddress) {
      siweCompletedRef.current = storedCompletedAddress;
      console.log('[AuthContext] 🔵 Restored SIWE completion from storage:', storedCompletedAddress);
    }

    if (storedWallet && storedApiKey) {
      siweCompletedRef.current = storedWallet.toLowerCase();
      console.log('[AuthContext] 🔵 Restored wallet session from storage:', storedWallet.substring(0, 10) + '...');
    }
  }, []);

  // Handle successful auth result from Bridge (moved outside useEffect for reuse)
  const handleAuthSuccess = useCallback(async (result) => {
    try {
      console.log('[AuthContext] 🔐 handleAuthSuccess called:', {
        platform: result.platform,
        address: result.address ? result.address.substring(0, 10) + '...' : 'NONE',
        hasSignature: !!result.signature,
        authType: result.authType,
        fid: result.fid
      });

      // For FID-only auth (no address), we need special handling
      if (!result.address && result.fid) {
        console.log('[AuthContext] 📲 FID-only auth (no wallet address):', result.fid);
        // For now, we can still create a session using just the FID
        // The backend will need to handle this case
      }

      // Determine auth type
      const authType = result.authType || 
        (result.signature ? 'signature' : (result.fid ? 'context' : 'unknown'));

      // 1. Send proof to backend. Farcaster uses a signed Quick Auth token;
      //    every wallet flow sends a SIWE signature.
      let loginResponse;
      if (result.platformToken && result.platform === 'farcaster') {
        loginResponse = await exchangeProviderToken('quick', result.platformToken);
      } else {
        loginResponse = await performLogin(
          null, // base URL (auto)
          result.address || `fid:${result.fid}`, // Use FID as pseudo-address if no real address
          result.signature, // Required: backend verifies the wallet signature
          null, // nonce (handled inside performLogin if needed)
          result.message, // Pass the actual signed message
          null, // sigType
          {
            platform: result.platform,
            fid: result.fid,
            username: result.username,
            authType: authType,
            ...result.context
          }
        );
      }

      const effectiveAddress =
        loginResponse?.wallet || result.address || (result.fid ? `fid:${result.fid}` : null);
      if (!effectiveAddress) {
        throw new Error('Authentication returned no identity');
      }

      // 2. Store credentials if returned
      if (loginResponse?.api_key) {
        setApiKey(loginResponse.api_key, effectiveAddress);
        localStorage.setItem('dcw_api_key', loginResponse.api_key);
      }
      if (loginResponse?.access_token) {
        localStorage.setItem('dcw_access_token', loginResponse.access_token);
      }
      if (loginResponse?.refresh_token) {
        localStorage.setItem('dcw_refresh_token', loginResponse.refresh_token); // Required for 401 refresh flow
      }
      setAuthWallet(effectiveAddress);
      localStorage.setItem('dcw_wallet', effectiveAddress.toLowerCase());

      // 3. Set State
      setNativeUser({
        username: result.username || effectiveAddress,
        custody_address: effectiveAddress,
        platform: result.platform,
        fid: result.fid,
        ...result
      });

      // 4. Mark SIWE as completed
      siweCompletedRef.current = effectiveAddress.toLowerCase();
      localStorage.setItem('dcw_siwe_completed', effectiveAddress.toLowerCase()); // Persist!

      console.log('[AuthContext] ✅ Auth success, user set:', result.username || result.address);
      return loginResponse;
    } catch (e) {
      console.error('[AuthContext] ❌ Login API failed:', e);
      setAuthError(e.message || 'Login failed');
      throw e;
    }
  }, []);

  // Fetch reputation for authenticated user
  const refreshReputation = useCallback(async (walletAddress) => {
    
    if (!walletAddress) {
      console.log('[AuthContext] No wallet address, skipping reputation fetch');
      return;
    }

    setIsLoadingReputation(true);
    try {
      console.log('[AuthContext] 🏆 Fetching reputation for:', walletAddress.substring(0, 10) + '...');
      const repData = await fetchReputation(walletAddress);
      setReputation(repData);
      console.log('[AuthContext] ✅ Reputation fetched:', repData.tier, `(score: ${repData.overall_score})`);
    } catch (error) {
      console.error('[AuthContext] ❌ Failed to fetch reputation:', error);
      // Set neutral fallback on error
      setReputation({
        wallet_address: walletAddress,
        overall_score: 0.5,
        tier: 'Neutral',
        tier_color: '#6c757d',
        tier_name: 'Block Boss',
        factor_scores: {},
        benefits: { loan_discount: 0, max_loan: 500 },
      });
    } finally {
      setIsLoadingReputation(false);
    }
  }, []);

  // Initialize AuthBridge & Listen for Context
  useEffect(() => {
    const init = async () => {
      await authBridge.initAuthBridge();
      setIsNativeLoading(false);

      // Attempt auto-auth for Farcaster/Base context
      try {
        const platform = authBridge.getPlatform();
        if (platform === PLATFORM.FARCASTER) {
          const result = await authBridge.authenticate();
          if (result && result.context) {
            console.log('[AuthContext] 🟣 Native Auto-Login Success');
            await handleAuthSuccess(result);
          }
        }
      } catch (e) {
        console.log('[AuthContext] No silent auth available');
      }
    };

    init();

    // Subscribe to context updates (Reactive Auto-Login)
    const unsubscribe = authBridge.onContext(async (ctx) => {
      console.log('[AuthContext] 🟣 Native Context Received:', ctx);

      // If we are in Farcaster and receive context with an FID/Address, auto-login
      if (ctx?.user?.fid && ctx?.user?.custodyAddress) {
        console.log('[AuthContext] ⚡ Reactive Auto-Login triggered by Farcaster Context');
        // We construct a synthetic result to pass to handleAuthSuccess
        // This avoids a full re-authentication call if we already have the data
        try {
          const bridgeResult = await authBridge.authenticate({ platform: 'farcaster' });
          await handleAuthSuccess(bridgeResult);
        } catch (e) {
          console.warn('[AuthContext] Auto-login failed', e);
        }
      }
    });

    return () => unsubscribe();
  }, [handleAuthSuccess]);

  // CRITICAL FIX: Complete SIWE login after RainbowKit/Wagmi connection
  // This effect runs when a wallet connects via RainbowKit
  // Use a ref to track if SIWE is currently in progress to prevent double execution
  const [loginNonce, setLoginNonce] = useState(0);
  const siweInProgressRef = useRef(false);
  const manualLoginRequestedRef = useRef(false);
  const siweAttemptedAddressRef = useRef(null);
  const signMessageRef = useRef(signMessageAsync);
  const siweFailureTimestampRef = useRef(null); // Track last failure to prevent loops

  useEffect(() => {
    signMessageRef.current = signMessageAsync;
  }, [signMessageAsync]);
  
  useEffect(() => {
    // ULTRA-DEBUG: Log every trigger of this effect
    console.log('[AuthContext] 🚨 SIWE useEffect RUNNING', {
      isConnected,
      address: address?.substring(0, 10),
      siweCompleted: siweCompletedRef.current,
      siweInProgress: siweInProgressRef.current,
      storedCompleted: localStorage.getItem('dcw_siwe_completed'),
      storedApiKey: !!localStorage.getItem('dcw_api_key'),
      storedWallet: localStorage.getItem('dcw_wallet')?.substring(0, 10),
      connector: !!connector
    });
    
    if (!isConnected || !address) {
      // Reset tracking when disconnected
      console.log('[AuthContext] 🔌 Wallet disconnected - resetting SIWE state');
      siweCompletedRef.current = null;
      siweInProgressRef.current = false;
      siweAttemptedAddressRef.current = null;
      return;
    }

    // Check if we already have a valid JWT for this address
    const existingApiKey = localStorage.getItem('dcw_api_key');
    const existingWallet = localStorage.getItem('dcw_wallet');
    const hasValidSession = existingApiKey && existingWallet && 
                            existingWallet.toLowerCase() === address.toLowerCase();
    
    console.log('[AuthContext] 🔍 Session check:', {
      hasApiKey: !!existingApiKey,
      hasWallet: !!existingWallet,
      walletMatch: existingWallet?.toLowerCase() === address.toLowerCase(),
      hasValidSession,
      siweCompleted: siweCompletedRef.current,
      storedCompleted: localStorage.getItem('dcw_siwe_completed')
    });
    
    
    console.log('[AuthContext] 🔍 Session check:', {
      hasApiKey: !!existingApiKey,
      hasWallet: !!existingWallet,
      walletMatch: existingWallet?.toLowerCase() === address.toLowerCase(),
      hasValidSession,
      connectorReady: !!connector,
      siweInProgress: siweInProgressRef.current
    });

    // If we have a valid session with JWT, mark as complete and update state
    if (hasValidSession) {
      
      console.log('[AuthContext] ✅ Valid existing session found for:', address);
      siweCompletedRef.current = address.toLowerCase();
      localStorage.setItem('dcw_siwe_completed', address.toLowerCase()); // Persist!
      
      // Ensure API key is set in memory too
      setApiKey(existingApiKey, address);
      setAuthWallet(address);
      
      // Ensure nativeUser state is set for UI consistency
      // NOTE: This used to be conditional, but we need it always to prevent loops
      // The useEffect dependency on nativeUser has been removed to prevent re-runs
      setNativeUser({
        username: address,
        custody_address: address,
        platform: 'web'
      });
      return;
    }

    if (!manualLoginRequestedRef.current) {
      return;
    }

    const normalizedAddress = address.toLowerCase();
    if (siweAttemptedAddressRef.current === normalizedAddress) {
      return;
    }
    siweAttemptedAddressRef.current = normalizedAddress;

    // CRITICAL: Prevent double execution with in-progress lock
    if (siweInProgressRef.current) {
      console.log('[AuthContext] 🔒 SIWE already in progress, skipping duplicate trigger');
      return;
    }

    // CRITICAL: Prevent loop if SIWE failed recently (30s cooldown)
    if (siweFailureTimestampRef.current) {
      const timeSinceFailure = Date.now() - siweFailureTimestampRef.current;
      const COOLDOWN_MS = 30000; // 30 seconds
      if (timeSinceFailure < COOLDOWN_MS) {
        const remainingSeconds = Math.ceil((COOLDOWN_MS - timeSinceFailure) / 1000);
        console.log(`[AuthContext] ⏸️ SIWE cooldown - retry in ${remainingSeconds}s`);
        return;
      } else {
        // Cooldown expired
        siweFailureTimestampRef.current = null;
      }
    }

    // Skip if we already completed SIWE for this address (check both ref and localStorage)
    // BUT only if we still have a valid API key - if the key was cleared, we need to re-auth
    const storedCompletedAddress = localStorage.getItem('dcw_siwe_completed');
    const isCompleted = siweCompletedRef.current === address.toLowerCase() || 
                        storedCompletedAddress === address.toLowerCase();
    
    if (isCompleted) {
      // CRITICAL FIX: Verify the API key still exists before skipping SIWE
      const currentApiKey = localStorage.getItem('dcw_api_key');
      if (currentApiKey) {
        console.log('[AuthContext] 🔵 SIWE already completed for:', address);
        // Ensure ref is set (might have been lost on re-render)
        siweCompletedRef.current = address.toLowerCase();
        return;
      } else {
        console.log('[AuthContext] ⚠️ SIWE was completed but API key is missing - re-authenticating');
        siweCompletedRef.current = null;
        localStorage.removeItem('dcw_siwe_completed');
      }
    }

    // Skip if this is a native SDK login (handled separately)
    // BUT only if we have a valid API key - otherwise need to re-auth
    if (nativeUser?.custody_address?.toLowerCase() === address.toLowerCase()) {
      const currentApiKey = localStorage.getItem('dcw_api_key');
      if (currentApiKey) {
        console.log('[AuthContext] 🔵 Address from native SDK, skipping SIWE');
        siweCompletedRef.current = address.toLowerCase();
        localStorage.setItem('dcw_siwe_completed', address.toLowerCase()); // Persist!
        return;
      } else {
        console.log('[AuthContext] ⚠️ Native SDK user but API key missing - will attempt SIWE');
      }
    }

    // CRITICAL: Skip SIWE for Base/World - they handle auth in their adapters
    const platform = authBridge.getPlatform();
    if (platform === PLATFORM.BASE_MINIKIT || platform === PLATFORM.WORLDCOIN) {
      console.log(`[AuthContext] 🔵 ${platform} detected - skipping auto-SIWE (handled by adapter)`);
      return;
    }

    // CRITICAL: Wait for connector to be fully initialized
    // Wagmi may report isConnected=true from cache before connector is ready
    if (!connector) {
      console.log('[AuthContext] ⏳ Connector not ready yet, waiting...');
      return; // Effect will re-run when connector becomes available
    }

    console.log('[AuthContext] 🟢 Wagmi wallet connected WITHOUT valid JWT, starting SIWE flow:', address);
    trackAuth('wallet_connected', { method: 'wagmi', wallet: address });
    
    
    // Set the lock immediately before async operations
    siweInProgressRef.current = true;
    
    // SAFETY: Auto-release lock after 60 seconds if something goes wrong
    const safetyTimeout = setTimeout(() => {
      if (siweInProgressRef.current) {
        console.log('[AuthContext] 🛡️ Safety timeout: releasing SIWE lock after 60s');
        siweInProgressRef.current = false;
      }
    }, 60000);

    // Complete SIWE authentication with backend
    const completeSiweLogin = async () => {
      try {
        setAuthError(null);

        // Small delay to ensure connector is fully initialized
        // This prevents "getChainId is not a function" errors
        await new Promise(resolve => setTimeout(resolve, 500));

        // Step 1: Get nonce from backend
        console.log('[AuthContext] 📡 Step 1: Fetching nonce...');
        const nonceData = await getNonce(address);
        
        if (!nonceData?.nonce || !nonceData?.message) {
          throw new Error('Failed to get nonce from backend');
        }

        console.log('[AuthContext] ✅ Got nonce:', nonceData.nonce.substring(0, 8) + '...');

        // Step 2: Sign message with wallet (with fallback for smart contract wallets)
        console.log('[AuthContext] ✍️ Step 2: Requesting signature...');
        let signature;
        
        try {
          // Primary method: wagmi signMessageAsync
          signature = await signMessageRef.current({ message: nonceData.message });
        } catch (signError) {
          console.warn('[AuthContext] ⚠️ signMessageAsync failed:', signError.message);
          
          // CRITICAL FIX: Fallback for Coinbase Smart Wallet and other EIP-1271 wallets
          // The "getChainId is not a function" error occurs with some connector implementations
          if (signError.message?.includes('getChainId') || 
              signError.message?.includes('is not a function') ||
              signError.message?.includes('connector')) {
            
            console.log('[AuthContext] 🔄 Attempting fallback signing via window.ethereum...');
            
            // Try to sign directly through window.ethereum (works for most wallets)
            if (window.ethereum && typeof window.ethereum.request === 'function') {
              try {
                // Use personal_sign RPC method directly
                signature = await window.ethereum.request({
                  method: 'personal_sign',
                  params: [nonceData.message, address],
                });
                console.log('[AuthContext] ✅ Fallback signing successful');
              } catch (fallbackError) {
                console.error('[AuthContext] ❌ Fallback signing also failed:', fallbackError);
                throw new Error(`Signature failed: ${signError.message}. Fallback also failed: ${fallbackError.message}`, { cause: fallbackError });
              }
            } else {
              // No ethereum provider available for fallback
              throw new Error(`Signature failed: ${signError.message}. No fallback provider available.`, { cause: signError });
            }
          } else {
            // Different error, not related to connector API - rethrow
            throw signError;
          }
        }
        
        if (!signature) {
          throw new Error('User rejected signature request');
        }

        console.log('[AuthContext] ✅ Got signature:', signature.substring(0, 20) + '...');

        // Step 3: Send signature to backend for verification
        console.log('[AuthContext] 📡 Step 3: Verifying with backend...');
        const loginResult = await signIn(signature, nonceData.message, address);
        
        if (!loginResult?.api_key && !loginResult?.access_token) {
          throw new Error('Backend verification failed - no tokens returned');
        }

        console.log('[AuthContext] ✅ Backend login successful!');

        // Step 4: Store credentials
        if (loginResult.api_key) {
          setApiKey(loginResult.api_key, address);
          localStorage.setItem('dcw_api_key', loginResult.api_key);
        }
        if (loginResult.access_token) {
          localStorage.setItem('dcw_access_token', loginResult.access_token);
        }
        setAuthWallet(address);
        localStorage.setItem('dcw_wallet', address.toLowerCase());

        // Step 5: Update state
        setNativeUser({
          username: address,
          custody_address: address,
          platform: 'web'
        });

        // Mark SIWE as completed for this address
        siweCompletedRef.current = address.toLowerCase();
        localStorage.setItem('dcw_siwe_completed', address.toLowerCase()); // Persist!
        siweFailureTimestampRef.current = null; // Clear failure timestamp on success

        trackAuth('login_success', { method: 'wagmi_siwe', wallet: address });
        console.log('[AuthContext] 🎉 SIWE Login Complete for:', address);

      } catch (error) {
        console.error('[AuthContext] ❌ SIWE login failed:', error);
        setAuthError(error.message || 'Authentication failed');
        trackAuth('login_failed', { method: 'wagmi_siwe', error: error.message });
        
        // CRITICAL: Set failure timestamp to prevent immediate retry loop
        siweFailureTimestampRef.current = Date.now();
        console.log('[AuthContext] 🛡️ SIWE cooldown activated - will retry in 30s');
        
        // Don't disconnect - let user try again
        // disconnect();
      } finally {
        // Always release the lock and clear safety timeout
        clearTimeout(safetyTimeout);
        siweInProgressRef.current = false;
      }
    };

    // Execute with error boundary
    completeSiweLogin().catch(err => {
      console.error('[AuthContext] ❌ Unhandled SIWE error:', err);
      clearTimeout(safetyTimeout);
      siweInProgressRef.current = false;
      siweFailureTimestampRef.current = Date.now();
      setAuthError(err.message || 'Authentication failed');
    });
  // CRITICAL FIX: Removed nativeUser from dependencies to prevent infinite re-renders
  // The SIWE completion state is now persisted in localStorage (dcw_siwe_completed)
  // and restored via the useEffect at component mount
  }, [isConnected, address, loginNonce]);



  // Unified Login Function
  const login = async () => {
    if (typeof window !== 'undefined') {
      window.__DCW_ALLOW_WALLET = true;
      setTimeout(() => { window.__DCW_ALLOW_WALLET = false; }, 180000);
    }
    manualLoginRequestedRef.current = true;
    siweAttemptedAddressRef.current = null;
    siweFailureTimestampRef.current = null;
    setLoginNonce((value) => value + 1);
    console.log('[AuthContext] 🔐 Login requested...');

    // =========================================================================
    // PRIORITY 1: Use early detection from index.html (most reliable)
    // =========================================================================
    const earlyDetection = typeof window !== 'undefined' ? window.DCW_EARLY_DETECTION : null;
    const isLocalHost = typeof window !== 'undefined' && /^(localhost|127\.0\.0\.1|\[::1\])$/.test(window.location.hostname);
    const explicitPlatform = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('platform') : null;
    const forceWebPlatform = isLocalHost && !explicitPlatform && import.meta.env.MODE !== 'test';
    const platformOverride = forceWebPlatform
      ? null
      : (typeof window !== 'undefined' ? window.DCW_PLATFORM_OVERRIDE : null);
    
    console.log('[AuthContext] 🔍 Early Detection:', earlyDetection);
    console.log('[AuthContext] 🔍 Platform Override:', platformOverride);
    
    let isFarcaster = false;
    let isWorld = false;
    let isBase = false;
    
    // Use override if available (set in index.html before React loads)
    if (platformOverride === 'farcaster') {
      isFarcaster = true;
      console.log('[AuthContext] ✅ Farcaster detected via PLATFORM_OVERRIDE');
    } else if (platformOverride === 'base-minikit') {
      isBase = true;
      console.log('[AuthContext] ✅ Base detected via PLATFORM_OVERRIDE');
    } else if (platformOverride === 'worldcoin') {
      isWorld = true;
      console.log('[AuthContext] ✅ World detected via PLATFORM_OVERRIDE');
    }
    
    // Use early detection if no override
    if (!forceWebPlatform && !isFarcaster && !isBase && !isWorld && earlyDetection) {
      if (earlyDetection.isFarcaster) {
        isFarcaster = true;
        console.log('[AuthContext] ✅ Farcaster detected via EARLY_DETECTION');
      } else if (earlyDetection.isBase) {
        isBase = true;
        console.log('[AuthContext] ✅ Base detected via EARLY_DETECTION');
      } else if (earlyDetection.isWorld) {
        isWorld = true;
        console.log('[AuthContext] ✅ World detected via EARLY_DETECTION');
      }
    }

    // =========================================================================
    // FALLBACK: Runtime detection if no early detection
    // =========================================================================
    if (!isFarcaster && !isBase && !isWorld) {
      const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
      const href = typeof window !== 'undefined' ? window.location.href : '';
      const referrer = typeof document !== 'undefined' ? document.referrer : '';
      
      console.log('[AuthContext] 🔍 Fallback detection - UA:', ua.substring(0, 80));
      
      // Farcaster
      if (/warpcast|farcaster/i.test(ua) || /warpcast|farcaster/i.test(referrer)) {
        isFarcaster = true;
        console.log('[AuthContext] ✅ Farcaster detected via runtime UA/referrer');
      }
      
      // Base - Check URL, referrer, AND ethereum provider
      if (!isFarcaster) {
        const eth = typeof window !== 'undefined' ? window.ethereum : null;
        const hasBaseMiniKit = typeof window !== 'undefined' && (!!window.baseMiniKit || !!eth?.isBaseMiniKit);
        const hasBaseUrl = /base\.app/i.test(href) || /base\.app/i.test(referrer);

        // A desktop Coinbase extension can expose isBase=true without being the
        // Base App. Only the Base MiniKit runtime or a Base URL is authoritative;
        // otherwise route to normal wallet SIWE.
        if (hasBaseMiniKit || hasBaseUrl) {
          isBase = true;
          console.log('[AuthContext] ✅ Base detected via runtime check:', { hasBaseMiniKit, hasBaseUrl });
        }
      }
      
      // World
      if (!isFarcaster && !isBase && /world\s*app|worldcoin/i.test(ua)) {
        isWorld = true;
        console.log('[AuthContext] ✅ World detected via runtime UA');
      }
      
      // Check authBridge as final fallback
      if (!isFarcaster && !isBase && !isWorld) {
        const bridgePlatform = authBridge.getPlatform();
        console.log('[AuthContext] 🔍 authBridge platform:', bridgePlatform);
        
        if (bridgePlatform === PLATFORM.FARCASTER) isFarcaster = true;
        else if (bridgePlatform === PLATFORM.BASE_MINIKIT) isBase = true;
        else if (bridgePlatform === PLATFORM.WORLDCOIN) isWorld = true;
      }
    }

    if (forceWebPlatform) {
      isFarcaster = false;
      isWorld = false;
      isBase = false;
    }

    console.log(`[AuthContext] 📊 FINAL DETECTION: Farcaster=${isFarcaster}, World=${isWorld}, Base=${isBase}`);

    // =========================================================================
    // ROUTE TO APPROPRIATE LOGIN METHOD
    // =========================================================================

    // Clear any previous errors before login attempt
    setAuthError(null);

    if (isFarcaster) {
      console.log('[AuthContext] 📲 Using FARCASTER native login');
      try {
        const result = await authBridge.authenticate({ platform: PLATFORM.FARCASTER });
        if (result) {
          await handleAuthSuccess(result);
          console.log('[AuthContext] ✅ Farcaster Login Success');
          trackAuth('login_success', { method: 'farcaster', wallet: result.address });
          return;
        }
        // Result was null - SDK might need more time
        console.warn('[AuthContext] ⚠️ Farcaster auth returned null - staying in Farcaster mode');
        setAuthError('Farcaster login incomplete - please try again');
        return; // DON'T fall through to RainbowKit in Farcaster context
      } catch (e) {
        console.warn('[AuthContext] ⚠️ Farcaster login failed:', e);
        setAuthError('Farcaster login failed: ' + (e.message || 'Unknown error'));
        trackAuth('login_failed', { method: 'farcaster', error: e.message });
        return; // DON'T fall through to RainbowKit in Farcaster context
      }
    }

    if (isWorld) {
      console.log('[AuthContext] 📲 Using WORLD APP native login');
      try {
        const result = await authBridge.authenticate({ platform: PLATFORM.WORLDCOIN });
        if (result) {
          await handleAuthSuccess(result);
          console.log('[AuthContext] ✅ World App Login Success');
          trackAuth('login_success', { method: 'worldcoin', wallet: result.address });
          return;
        }
      } catch (e) {
        console.warn('[AuthContext] ⚠️ World App login failed:', e);
        setAuthError('World App login failed: ' + (e.message || 'Unknown error'));
        trackAuth('login_failed', { method: 'worldcoin', error: e.message });
      }
    }

    if (isBase) {
      console.log('[AuthContext] 📲 Using BASE native login');
      try {
        const result = await authBridge.authenticate({ platform: PLATFORM.BASE_MINIKIT });
        if (result) {
          await handleAuthSuccess(result);
          console.log('[AuthContext] ✅ Base Login Success');
          trackAuth('login_success', { method: 'base', wallet: result.address });
          return;
        }
        // Result was null - Base SDK might need more time
        console.warn('[AuthContext] ⚠️ Base auth returned null - staying in Base mode');
        setAuthError('Base login incomplete - please try again');
        return; // DON'T fall through to RainbowKit in Base context
      } catch (e) {
        console.warn('[AuthContext] ⚠️ Base login failed:', e);
        setAuthError('Base login failed: ' + (e.message || 'Unknown error'));
        trackAuth('login_failed', { method: 'base', error: e.message });
        return; // DON'T fall through to RainbowKit in Base context
      }
    }

    // WEB / DESKTOP PATH - No native SDK detected
    if (isConnected && address) {
      console.log('[AuthContext] 🌐 Wallet already connected - SIWE will run from login intent');
      return;
    }
    console.log('[AuthContext] 🌐 No native SDK detected - using RainbowKit');
    if (openConnectModal) {
      openConnectModal();
    } else {
      console.error('[AuthContext] ❌ openConnectModal is UNDEFINED');
    }
  };

  const logout = async () => {
    
    console.log('[AuthContext] Logout requested');
    
    // Clear SIWE tracking to allow re-authentication
    siweCompletedRef.current = null;
    
    // Clear every credential and restore marker through one code path.
    clearStoredSession();
    localStorage.removeItem('dcw_platform_context');
    
    // Disconnect Wagmi
    disconnect();
    
    // Clear native user state
    setNativeUser(null);
    setAuthError(null);
    
    trackAuth('logout_success', { method: 'unified' });
    console.log('[AuthContext] 🔓 Logout complete - ready for re-login');
  };

  // Unified State
  const isAuthenticated = isConnected || !!nativeUser;
  // Prefer Wagmi address, fallback to native custody address
  const activeWallet = address || nativeUser?.custody_address;
  const displayUser = nativeUser?.username || (address ? `${address.slice(0, 6)}...` : null);

  // Fetch reputation when user becomes authenticated
  useEffect(() => {
    
    if (isAuthenticated && activeWallet && !reputation) {
      refreshReputation(activeWallet);
    }
  }, [isAuthenticated, activeWallet, reputation, refreshReputation]);

  return (
    <AuthContext.Provider
      value={{
        wallet: activeWallet,
        username: displayUser,
        isAuthenticated,
        loading: isConnecting || isNativeLoading,
        error: authError,
        login,
        logout,
        nativeUser,
        clearError: () => setAuthError(null),
        // Reputation integration
        reputation,
        isLoadingReputation,
        refreshReputation: () => refreshReputation(activeWallet),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};